// src/components/SignIn/SignIn.tsx
import React, { useState, FormEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginWithGoogle, logoutUser, loginWithEmailPassword } from '../../redux/slices/userSlice';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { RootState } from '../../redux/store';
import {
  Button,
  Paper,
  Typography,
  Grid,
  Alert,
  Collapse,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';

const SignInForm = () => {
  const dispatch: ThunkDispatch<RootState, void, AnyAction> = useDispatch();
  const { isAuthenticated, user, error } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/';
  const { t } = useUntypedTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for Email/Password fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailPasswordSignIn = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const result = await dispatch(loginWithEmailPassword({ email, password })).unwrap();
      if (result) {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Email/Password sign in failed:', error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await dispatch(loginWithGoogle()).unwrap();
      if (result) {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Google sign in failed:', error);
    }
  };

  const handleSignOut = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        width: isMobile ? '100%' : '100%',
        px: isMobile ? 1 : 4,
        border: 'none',
        boxShadow: 'none',
      }}
    >
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 4,
            textAlign: 'center',
            borderRadius: { xs: 2, sm: 3 },
            backgroundColor: 'background.paper',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, color: 'primary.main' }}>
            {isAuthenticated ? `${t('signin.welcome')}, ${user?.username}!` : t('signin.title')}
          </Typography>

          <Collapse in={!!error}>
            <Alert severity="error" sx={{ mb: 2.5, textAlign: 'left' }}>
              {error?.includes('domain')
                ? 'Access denied: Google sign-in is only available for @gaialogic.ai and @ddductr.de email addresses.'
                : error?.includes('auth/invalid-credential')
                ? t('auth.errors.invalidCredential')
                : error}
            </Alert>
          </Collapse>

          {!isAuthenticated && (
            <Box
              component="form"
              onSubmit={handleEmailPasswordSignIn}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mb: 3,
              }}
            >
              <TextField
                type="email"
                label={t('signin.email')}
                variant="outlined"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <TextField
                type="password"
                label={t('signin.password')}
                variant="outlined"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <Box sx={{ textAlign: 'right', mt: -1, mb: 1 }}>
                <Typography
                  component={Link}
                  to="/reset-password-request"
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  {t('signin.forgotPassword')}
                </Typography>
              </Box>
              <Button variant="contained" type="submit" fullWidth>
                {t('signin.submit')}
              </Button>
            </Box>
          )}

          <Typography variant="body2" sx={{ mb: 2, marginTop: 10 }}>
            {isAuthenticated
              ? t('signin.signedInWithGoogle')
              : t('signin.googleInfo')}
          </Typography>

          <Button
            onClick={isAuthenticated ? handleSignOut : handleGoogleSignIn}
            variant={isAuthenticated ? 'outlined' : 'contained'}
            color={isAuthenticated ? 'secondary' : 'primary'}
            startIcon={isAuthenticated ? <LogoutIcon /> : <GoogleIcon />}
            fullWidth
            sx={{
              py: { xs: 1, sm: 1.25 },
              transition: 'background-color 0.3s ease',
            }}
          >
            {isAuthenticated ? t('navigation.signout') : t('signin.googleSignIn')}
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SignInForm;
